import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Truck, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

import API from "@/utils/api";

export default function DriverLogin() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Introduce email y contraseña");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password },
        {
          withCredentials: true,
          timeout: 10000 // 10 segundos de timeout
        }
      );

      const userData = response.data.user;
      setUser(userData);

      // Store in localStorage for persistence
      localStorage.setItem("user", JSON.stringify(userData));

      toast.success(`¡Bienvenido, ${userData.name}!`);

      // Redirect based on role
      if (userData.role === "admin") {
        navigate("/admin");
      } else if (userData.role === "driver") {
        navigate("/driver");
      } else {
        navigate("/");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Error al iniciar sesión";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-yellow-900/20 via-black to-black" />

      <Card className="w-full max-w-md bg-gray-900 border-gray-800 relative z-10">
        <CardHeader className="text-center space-y-4">
          <Button
            variant="ghost"
            size="sm"
            className="absolute left-4 top-4 text-gray-400 hover:text-white"
            onClick={() => navigate("/")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>

          <div className="w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mx-auto">
            <Truck className="w-10 h-10 text-black" />
          </div>
          <CardTitle className="text-2xl font-heading text-white">
            Acceso Repartidores
          </CardTitle>
          <CardDescription className="text-gray-400">
            Introduce tus credenciales para acceder al panel
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Email</label>
              <Input
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="driver-login-email"
              />
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-1 block">Contraseña</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white pr-10"
                  data-testid="driver-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold h-12"
              disabled={isLoading}
              data-testid="driver-login-submit"
            >
              {isLoading ? "Iniciando sesión..." : "Iniciar Sesión"}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-800">
            <p className="text-center text-gray-500 text-sm">
              ¿No tienes cuenta? Contacta con el administrador
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
